/**
 * Theme Configuration Builder
 * 
 * Creates Material-UI themes integrated with our semantic token system
 */

import { createTheme, Theme } from '@mui/material/styles';
import { arSD } from '@mui/x-data-grid/locales';
import { arSD as coreArSD } from '@mui/material/locale';
import { ThemeConfig, ExtendedMuiTheme, AmeenTheme } from './themes/types';
import { lightTheme } from './themes/light.theme';
import { darkTheme } from './themes/dark.theme';
import { createButtonTheme, createTypographyTheme } from './components';

/**
 * Get the Ameen theme based on mode
 */
export function getAmeenTheme(mode: 'light' | 'dark'): AmeenTheme {
  return mode === 'dark' ? darkTheme : lightTheme;
}

/**
 * Create a Material-UI theme integrated with our semantic tokens
 */
export function createAmeenTheme(config: ThemeConfig): ExtendedMuiTheme {
  const { mode, direction = 'ltr', language = 'en' } = config;
  const isRtl = direction === 'rtl';
  const isAr = language === 'ar';
  const ameenTheme = getAmeenTheme(mode);
  const { colors } = ameenTheme;

  // Create MUI theme with our customizations
  const muiTheme = createTheme(
    {
      direction: isRtl ? 'rtl' : 'ltr',
      
      palette: {
        mode,
        
        primary: {
          main: colors.brand.primary,
          contrastText: colors.text.inverse,
        },
        secondary: {
          main: colors.brand.secondary,
          contrastText: colors.text.inverse,
        },
        error: {
          main: colors.status.error,
          contrastText: colors.text.inverse,
        },
        warning: {
          main: colors.status.warning,
          contrastText: colors.text.inverse,
        },
        info: {
          main: colors.status.info,
          contrastText: colors.text.inverse,
        },
        success: {
          main: colors.status.success,
          contrastText: colors.text.inverse,
        },
        background: {
          default: colors.background.primary,
          paper: colors.background.secondary,
        },
        text: {
          primary: colors.text.primary,
          secondary: colors.text.secondary,
          disabled: colors.text.disabled,
        },
        divider: colors.special.divider,
      },

      typography: {
        fontFamily: isAr ? ameenTheme.typography.fontFamilyAr : ameenTheme.typography.fontFamily,
        fontSize: 12,
        button: {
          textTransform: 'none',
        },
      },

      shape: {
        borderRadius: 8,
      },

      components: {
        // Paper
        // MuiPaper: {
        //   styleOverrides: {
        //     root: {
        //       boxShadow: colors.components.card.shadow,
        //     },
        //   },
        // },

        // Button - Using centralized button theme with all variants
        MuiButton: createButtonTheme(colors),

        // Typography - Using centralized typography theme with all variants
        MuiTypography: createTypographyTheme({ colors, isRtl }),

        // Icon Button
        // MuiIconButton: {
        //   styleOverrides: {
        //     root: {
        //       '&:hover': {
        //         backgroundColor: colors.background.hover,
        //       },
        //     },
        //   },
        // },

        // Input
        MuiInput: {
          styleOverrides: {
            input: ({ ownerState }) => ({
              ':placeholder-shown': { color: colors.components.input.placeholder },
              color: colors.components.input.text,
              fontSize: ameenTheme.typography.fontSize.base,
              fontWeight: ameenTheme.typography.fontWeight.light,
              textAlign: ownerState.type === 'number' && !isRtl ? 'right' : undefined,
            }),
          },
        },

        // Outlined Input
        MuiOutlinedInput: {
          styleOverrides: {
            input: ({ ownerState }) => ({
              ':placeholder-shown': { color: colors.components.input.placeholder },
              color: colors.components.input.text,
              fontSize: ameenTheme.typography.fontSize.base,
              fontWeight: ameenTheme.typography.fontWeight.light,
              textAlign: ownerState.type === 'number' ? 'right' : undefined,
            }),
            root: {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: colors.components.input.border,
              },
            },
          },
        },

        // TextField
        // MuiTextField: {
        //   defaultProps: {
        //     InputLabelProps: {
        //       style: {
        //         fontSize: ameenTheme.typography.fontSize.base,
        //       },
        //     },
        //     sx: mode === 'dark' ? {
        //       '& .MuiInputBase-input': {
        //         color: colors.components.input.text,
        //       },
        //       '& .MuiOutlinedInput-root': {
        //         '& .MuiOutlinedInput-notchedOutline': {
        //           borderColor: colors.components.input.border,
        //         },
        //         '&:hover .MuiOutlinedInput-notchedOutline': {
        //           borderColor: colors.components.input.borderHover,
        //         },
        //         '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        //           borderColor: colors.components.input.borderFocus,
        //         },
        //         '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        //           borderColor: colors.components.input.border,
        //         },
        //       },
        //       '& .MuiInputLabel-root': {
        //         color: colors.text.primary,
        //       },
        //       '& .MuiInputLabel-root.Mui-focused': {
        //         color: colors.brand.primary,
        //       },
        //     } : undefined,
        //   },
        // },

        // Select
        // MuiSelect: {
        //   defaultProps: {
        //     sx: mode === 'dark' ? {
        //       '& .MuiInputBase-input': {
        //         color: colors.components.input.text,
        //       },
        //       '& .MuiOutlinedInput-root': {
        //         '& .MuiOutlinedInput-notchedOutline': {
        //           borderColor: colors.components.input.border,
        //         },
        //         '&:hover .MuiOutlinedInput-notchedOutline': {
        //           borderColor: colors.components.input.borderHover,
        //         },
        //         '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        //           borderColor: colors.components.input.borderFocus,
        //         },
        //         '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
        //           borderColor: colors.components.input.border,
        //         },
        //       },
        //       '& .MuiInputLabel-root': {
        //         color: colors.text.primary,
        //       },
        //       '& .MuiInputLabel-root.Mui-focused': {
        //         color: colors.brand.primary,
        //       },
        //     } : undefined,
        //   },
        // },

        // Input Label
        // MuiInputLabel: {
        //   styleOverrides: {
        //     root: {
        //       color: colors.text.primary,
        //       backgroundColor: colors.background.primary,
        //     },
        //   },
        // },

        // Form Label
        // MuiFormLabel: {
        //   styleOverrides: {
        //     asterisk: {
        //       color: colors.status.error,
        //     },
        //   },
        // },

        // // Form Control Label
        // MuiFormControlLabel: {
        //   styleOverrides: {
        //     label: {
        //       fontSize: ameenTheme.typography.fontSize.base,
        //       fontWeight: ameenTheme.typography.fontWeight.light,
        //     },
        //   },
        // },

        // Form Helper Text
        // MuiFormHelperText: {
        //   styleOverrides: {
        //     root: { 
        //       maxWidth: 'auto', 
        //       fontSize: ameenTheme.typography.fontSize.xs,
        //     },
        //   },
        // },

        // AppBar
        MuiAppBar: {
          styleOverrides: {
            colorPrimary: {
              backgroundColor: colors.components.navigation.background,
              color: colors.components.navigation.text,
            },
          },
        },
        // MuiIcon: {
        //   styleOverrides: {
        //     root: {
        //       color: colors.components.icon.color,
        //       '&:hover': {
        //         color: colors.components.icon.hover,
        //       },
        //       '&:active': {
        //         color: colors.components.icon.active,
        //       },
        //       '&:disabled': {
        //         color: colors.components.icon.disabled,
        //       },
        //     },
        //   },
        // },

        // SvgIcon - for @mui/icons-material icons like ShoppingBagOutlinedIcon
        MuiSvgIcon: {
          styleOverrides: {
            root: {
              // color: colors.components.icon.color,
              // backgroundColor: colors.background.primary,
              // '&:hover': {
              //   color: colors.components.icon.hover,
              //   // backgroundColor: colors.background.hover,
              // },
              // '&:active': {
              //   color: colors.components.icon.active,
              // },
              // '&.Mui-disabled': {
              //   color: colors.components.icon.disabled,
              // },
            },
          },
        },

        // // IconButton - handle icon colors inside icon buttons
        // MuiIconButton: {
        //   styleOverrides: {
        //     root: {
        //       color: colors.components.icon.color,
        //       '&:hover': {
        //         color: colors.components.icon.hover,
        //         backgroundColor: colors.background.hover,
        //       },
        //       '&:active': {
        //         color: colors.components.icon.active,
        //       },
        //       '&.Mui-disabled': {
        //         color: colors.components.icon.disabled,
        //       },
        //     },
        //   },
        // },

        // Drawer
        MuiDrawer: {
          styleOverrides: {
            paper: {
              backgroundColor: colors.components.drawer.background,
              color: colors.components.drawer.text,
            },
          },
        },

        
        MuiBackdrop: {
          styleOverrides: {
            root: {
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
            },
          },
        },

        // Dialog
        MuiDialog: {
          styleOverrides: {
            paper: {
              overflow: 'visible',
              boxShadow: 'none',
              // background: colors.components.dialog.background,
            },
          },
        },

        // Dialog Title
        // MuiDialogTitle: {
        //   styleOverrides: {
        //     root: {
        //       background: colors.components.dialog.background,
        //     },
        //   },
        // },

        // // Card Header
        // MuiCardHeader: {
        //   styleOverrides: {
        //     title: {
        //       fontSize: ameenTheme.typography.fontSize.lg,
        //       color: colors.components.card.headerText,
        //     },
        //   },
        // },

        // Autocomplete
        // MuiAutocomplete: {
        //   styleOverrides: {
        //     listbox: {
        //       fontSize: ameenTheme.typography.fontSize.base,
        //       fontWeight: ameenTheme.typography.fontWeight.light,
        //     },
        //     popupIndicator: {
        //       color: colors.text.secondary,
        //       '& svg': {
        //         fontSize: '24px',
        //       },
        //     },
        //     root: {
        //       color: colors.text.primary,
        //       borderRadius: ameenTheme.borderRadius.md,
        //       '& .MuiInputBase-root': {
        //         color: colors.text.primary,
        //         backgroundColor: colors.background.primary,
        //       },
        //       '& .MuiOutlinedInput-notchedOutline': {
        //         borderColor: mode === 'dark' ? colors.components.input.border : undefined,
        //       },
        //       '&:hover .MuiOutlinedInput-notchedOutline': {
        //         borderColor: mode === 'dark' ? colors.components.input.borderHover : undefined,
        //       },
        //       '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        //         borderColor: mode === 'dark' ? colors.components.input.borderFocus : undefined,
        //       },
        //     },
        //   },
        // },

        // Radio
        MuiRadio: {
          styleOverrides: {
            root: {
              color: colors.components.checkbox.border,
              '&.Mui-checked': {
                color: colors.components.checkbox.checked,
              },
            },
          },
        },

        // Checkbox
        MuiCheckbox: {
          styleOverrides: {
            colorPrimary: {
              // background: colors.components.checkbox.background,
              padding: 0,
              borderRadius: ameenTheme.borderRadius.sm,
              color: colors.components.checkbox.background,
            },
          },
        },

        // Tab
        MuiTab: {
          styleOverrides: {
            root: {
              fontStyle: 'normal',
              fontWeight: ameenTheme.typography.fontWeight.medium,
              lineHeight: 'normal',
              fontSize: ameenTheme.typography.fontSize.base,
              padding: '1rem',
              color: colors.components.tab.text,
              '&.Mui-selected': {
                backgroundColor: colors.components.tab.activeBackground,
              },
            },
          },
        },

        // Grid
        MuiGrid: {
          styleOverrides: {
            root: ({ ownerState }: { ownerState: { container?: boolean } }) => ({
              ...(!ownerState.container && { paddingBottom: '16px' }),
            }),
          },
        },

        // Typography
        // MuiTypography: {
        //   styleOverrides: {
        //     root: {
        //       color: mode === 'dark' ? '' : colors.text.primary,
        //     },
        //   },
        // },
      },
    },
    // Localization for Arabic
    ...(isAr ? [arSD, coreArSD] : [])
  );

  // Add our custom theme properties
  const extendedTheme = muiTheme as ExtendedMuiTheme;
  extendedTheme.ameen = ameenTheme;

  return extendedTheme;
}

